import { BASE_ACCESS_ROLE } from '@trustero/trustero-api-web/lib/authz/authz_pb'
import {
  CAPABILITY,
  PERMISSION_RULE,
} from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import type { Permissions } from 'src/context/authContext'

/**
 * An actor-only capability check, for the two places that cannot call a hook: the auth
 * reducer, and a guard that must ask about the *authentic* role while a preview is
 * active. Only an unconditional grant answers true - a rule that depends on a resource
 * has no resource here, so it fails closed. Anywhere a resource exists, use
 * `useCanPerformAction`.
 */
export const hasUnconditionalCapability = (
  permissions: Permissions | undefined,
  capability: CAPABILITY,
): boolean =>
  permissions?.capabilities?.[capability]?.rule === PERMISSION_RULE.ALWAYS

export const isAuditorBase = (base?: BASE_ACCESS_ROLE): boolean =>
  base === BASE_ACCESS_ROLE.AUDITOR || base === BASE_ACCESS_ROLE.AUDITOR_QC

/**
 * Role shape, for the chrome that varies by it: banner copy and full-width layout.
 * A custom role inherits its base from the role it was created on, so this stays
 * true of custom roles. What a user may *do* is always a capability question.
 */
export const isBase = (
  permissions: Permissions | undefined,
  base: BASE_ACCESS_ROLE,
): boolean => permissions?.baseAccessRole === base

/**
 * Read-only in effect: the base grants no writes and the role adds nothing on top.
 * A READ_ONLY base carrying overrides can write, so it is not one of these.
 */
export const isEffectivelyReadOnly = (permissions?: Permissions): boolean =>
  isBase(permissions, BASE_ACCESS_ROLE.READ_ONLY) &&
  permissions?.hasRoleOverrides === false

export const getRoleBannerMessage = (permissions?: Permissions): string => {
  const base = permissions?.baseAccessRole
  if (isAuditorBase(base)) {
    return 'You have auditor access. Please contact your account administrator if you need additional access.'
  }
  if (isEffectivelyReadOnly(permissions)) {
    return 'You have read-only access to this account.'
  }
  if (permissions?.hasRoleOverrides === false) {
    switch (base) {
      case BASE_ACCESS_ROLE.TP_PUBLISHER:
        return 'You have Trust Portal Publisher access. Please contact your account administrator if you need additional access.'
      case BASE_ACCESS_ROLE.TP_APPROVER:
        return 'You have Trust Portal Approver access. Please contact your account administrator if you need additional access.'
    }
  }
  return ''
}
